import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { Checkbox, DialogActions, DialogContent, DialogTitle, LinearProgress } from '@mui/material';
import { GridToolbarColumnsButton, GridToolbarContainer, GridToolbarDensitySelector, GridToolbarFilterButton } from '@mui/x-data-grid';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { USER_GROUP } from '../../../assets/data/UserGroupsData';
import NoRowOverlay from '../../../assets/images/icons/NoRowOverlay';
import { ControlpanelPrimaryButton, ControlpanelSecondaryButton, GrayIconButton } from '../../../components/StyledButton';
import { CustomDialog, StyledDataGrid } from '../../../components/StyledComponents';
import { generateTranslateMuiTable } from '../../../utils/Function/commonFunction';
import CustomGridToolbarQuickFilter from '../CustomGridToolbarQuickFilter';
import CustomPagination from '../CustomPagination';
import CustomColumnsPanel from '../../../components/CustomColumnsPanel';

const CustomToolbar = () => {
   return (
      <GridToolbarContainer className='flex justify-between'>
         <div>
            <GridToolbarColumnsButton />
            <GridToolbarFilterButton />
            <GridToolbarDensitySelector />
         </div>
         <CustomGridToolbarQuickFilter />
      </GridToolbarContainer>
   );
};

const SelectUserGroupsDialog = ({ onClose, onSubmit }) => {
   const { t } = useTranslation();
   const tableTranslated = useMemo(() => generateTranslateMuiTable(t), [t]);
   const [rowData, setRowData] = useState(USER_GROUP);
   const [page, setPage] = useState(0);
   const [pageSize, setPageSize] = useState(20);

   const handleCheckboxChange = useCallback((id, field, checked, isCheckAll = false) => {
      setRowData((prev) => {
         prev.forEach((v) => {
            if (isCheckAll) {
               v[field] = false;
               if (v.groupCode === id && checked) {
                  v.selected = checked;
               }
            }

            if (v.groupCode === id) {
               v[field] = checked;
            }
         });

         return prev.slice();
      });
   }, []);

   const handleConfirm = () => {
      onSubmit?.(rowData);
      onClose();
   };

   const columnData = useMemo(
      () => [
         { field: 'groupCode', headerName: t('common.content-077'), flex: 1 },
         { field: 'description', headerName: t('common.description'), flex: 1 },
         { field: 'manager', headerName: t('common.content-090'), flex: 1 },
         {
            field: 'selected',
            headerName: t('common.content-077'),
            flex: 1,
            renderCell: ({ value, id, field }) => (
               <Checkbox checked={value} onChange={(event) => handleCheckboxChange(id, field, event.target.checked)} />
            ),
         },
         {
            field: 'primaryGroup',
            headerName: t('common.content-091'),
            flex: 1,
            renderCell: ({ value, id, field }) => (
               <Checkbox checked={value} onChange={(event) => handleCheckboxChange(id, field, event.target.checked, true)} />
            ),
         },
      ],
      [t, handleCheckboxChange]
   );

   return (
      <CustomDialog maxWidth={'xl'} open onClose={onClose} keepMounted>
         <DialogTitle>
            <div className='flex justify-between'>
               <div className='flex items-center gap-4'>
                  <p className='text-xl font-semibold '>{t('common.select-user-groups')}</p>
               </div>
               <GrayIconButton onClick={onClose}>
                  <CloseIcon />
               </GrayIconButton>
            </div>
         </DialogTitle>
         <DialogContent>
            <div className='max-h-[400px]'>
               <StyledDataGrid
                  disableRowSelectionOnClick
                  rows={rowData}
                  columns={columnData}
                  getRowId={(v) => v.groupCode}
                  slots={{
                     toolbar: CustomToolbar,
                     pagination: CustomPagination,
                     loadingOverlay: LinearProgress,
                     noRowsOverlay: NoRowOverlay,
                     columnsPanel: CustomColumnsPanel,
                  }}
                  paginationModel={{ page, pageSize }}
                  slotProps={{
                     pagination: {
                        page,
                        count: rowData.length,
                        rowsPerPage: pageSize,
                        onPageChange: setPage,
                        onRowsPerPageChange: setPageSize,
                     },
                  }}
                  pa
                  localeText={tableTranslated}
               />
            </div>
         </DialogContent>
         <DialogActions>
            <div className='flex gap-2 px-4 py-4'>
               <ControlpanelSecondaryButton onClick={onClose}>
                  <CloseIcon />
                  <p>{t('button.close')}</p>
               </ControlpanelSecondaryButton>
               <ControlpanelPrimaryButton onClick={handleConfirm}>
                  <EditIcon />
                  <p>{t('button.update')}</p>
               </ControlpanelPrimaryButton>
            </div>
         </DialogActions>
      </CustomDialog>
   );
};

export default SelectUserGroupsDialog;
